// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type {
  BankUploadSection,
  BankUploadStep,
} from '@client/banks/types/bank-upload';

/** Data sections shown as drawer chrome tabs (excludes institution / review). */
export type BankUploadDataSection = Extract<
  BankUploadSection,
  'accounts' | 'positions' | 'debts' | 'transactions'
>;

export type BankUploadSectionTab = {
  section: BankUploadDataSection;
  listStep: BankUploadStep;
  labelKey:
    | 'aggregator.create-bank.steps.accounts'
    | 'aggregator.create-bank.steps.positions'
    | 'aggregator.create-bank.steps.debts'
    | 'aggregator.create-bank.steps.transactions';
};

export const BANK_UPLOAD_SECTION_TABS = [
  {
    section: 'accounts',
    listStep: 'accounts-list',
    labelKey: 'aggregator.create-bank.steps.accounts',
  },
  {
    section: 'positions',
    listStep: 'positions-list',
    labelKey: 'aggregator.create-bank.steps.positions',
  },
  {
    section: 'debts',
    listStep: 'debts-list',
    labelKey: 'aggregator.create-bank.steps.debts',
  },
  {
    section: 'transactions',
    listStep: 'transactions-list',
    labelKey: 'aggregator.create-bank.steps.transactions',
  },
] as const satisfies readonly BankUploadSectionTab[];

export const BANK_UPLOAD_DATA_SECTIONS: readonly BankUploadDataSection[] =
  BANK_UPLOAD_SECTION_TABS.map(tab => tab.section);

export const isBankUploadDataSection = (
  section: BankUploadSection | undefined,
): section is BankUploadDataSection =>
  section !== undefined &&
  (BANK_UPLOAD_DATA_SECTIONS as readonly string[]).includes(section);

export const getListStepForDataSection = (
  section: BankUploadDataSection,
): BankUploadStep => {
  const tab = BANK_UPLOAD_SECTION_TABS.find(item => item.section === section);

  return tab?.listStep ?? 'accounts-list';
};
